import { z } from 'zod';
import { FILE_KINDS, FILE_STATUSES, MOVEMENT_PURPOSES } from './domain.js';
import { ROLES } from './roles.js';
/** Trim, and turn empty strings into null. Old files have many blank fields. */
const blankToNull = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const requiredText = (max, field) => z.string().trim().min(1, `${field} is required`).max(max);
/** Indian mobile number: 10 digits starting 6-9, with optional +91 / 0 prefix. */
export const mobileSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .refine((v) => /^(?:\+91|91|0)?[6-9]\d{9}$/.test(v), {
    message: 'Enter a valid 10-digit Indian mobile number',
})
    .transform((v) => v.replace(/^(?:\+91|91|0)/, ''));
// ---------------------------------------------------------------------------
// File records
// ---------------------------------------------------------------------------
export const storageLocationInput = z.object({
    roomCode: requiredText(32, 'Room'),
    rackCode: requiredText(32, 'Rack'),
    columnCode: requiredText(32, 'Column'),
    bagCode: requiredText(32, 'Bag'),
});
export const fileRecordInput = z.object({
    kind: z.enum(FILE_KINDS),
    // Identity
    fileNumber: blankToNull(120),
    fileName: blankToNull(300),
    subjectEn: blankToNull(300),
    subjectHi: blankToNull(300),
    // Ownership / classification
    departmentId: z.number().int().positive().nullable().optional().transform((v) => v ?? null),
    zoneId: z.number().int().positive().nullable().optional().transform((v) => v ?? null),
    wardNo: blankToNull(16),
    oicName: blankToNull(120),
    financialYear: blankToNull(16),
    // Property block (Form 2 only -- nullable for general files)
    schemeNo: blankToNull(64),
    schemeNameEn: blankToNull(200),
    schemeNameHi: blankToNull(200),
    societyName: blankToNull(200),
    sector: blankToNull(64),
    plotNo: blankToNull(64),
    houseNo: blankToNull(64),
    firstAllotteeEn: blankToNull(200),
    firstAllotteeHi: blankToNull(200),
    leaseAcNo: blankToNull(64),
    leaseDescription: blankToNull(500),
    remark: blankToNull(1000),
    location: storageLocationInput,
});
export const fileRecordPatch = fileRecordInput.partial().extend({
    location: storageLocationInput.optional(),
});
// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------
export const SEARCH_SORTS = ['relevance', 'recent', 'fileNumber'];
export const searchQuery = z.object({
    q: z.string().trim().max(200).default(''),
    status: z.enum(FILE_STATUSES).optional(),
    kind: z.enum(FILE_KINDS).optional(),
    zoneId: z.coerce.number().int().positive().optional(),
    departmentId: z.coerce.number().int().positive().optional(),
    wardNo: z.string().trim().max(16).optional(),
    yearFrom: z.coerce.number().int().min(1900).max(2200).optional(),
    yearTo: z.coerce.number().int().min(1900).max(2200).optional(),
    roomCode: z.string().trim().max(32).optional(),
    rackCode: z.string().trim().max(32).optional(),
    bagId: z.coerce.number().int().positive().optional(),
    sort: z.enum(SEARCH_SORTS).default('relevance'),
    /** Opaque keyset cursor from the previous page. Never an offset. */
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
/** Which search strategy answered the query -- surfaced in the UI and in logs. */
export const SEARCH_STRATEGIES = ['exact', 'fulltext', 'fuzzy', 'browse'];
// ---------------------------------------------------------------------------
// Movements (issue / return)
// ---------------------------------------------------------------------------
export const issueRequestSchema = z.object({
    fileId: z.coerce.number().int().positive(),
    issuedToUserId: z.coerce.number().int().positive(),
    purpose: z.enum(MOVEMENT_PURPOSES),
    purposeNote: blankToNull(500),
    expectedReturnDate: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Invalid date'),
});
export const otpVerifySchema = z.object({
    challengeId: z.string().uuid(),
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
export const returnRequestSchema = z.object({
    fileId: z.coerce.number().int().positive(),
    /** Re-confirmed shelf address. Staff may re-file it somewhere else. */
    location: storageLocationInput.optional(),
    condition: z.enum(['GOOD', 'DAMAGED', 'INCOMPLETE']).default('GOOD'),
    note: blankToNull(500),
});
// ---------------------------------------------------------------------------
// Users & auth
// ---------------------------------------------------------------------------
export const loginSchema = z.object({
    username: requiredText(64, 'Username'),
    password: z.string().min(1, 'Password is required').max(200),
});
export const userCreateSchema = z.object({
    username: z
        .string()
        .trim()
        .min(3)
        .max(64)
        .regex(/^[a-z0-9._-]+$/i, 'Letters, digits, dot, underscore and hyphen only'),
    fullName: requiredText(120, 'Full name'),
    fullNameHi: blankToNull(120),
    mobile: mobileSchema,
    email: z.string().trim().email().max(200).nullable().optional().transform((v) => v ?? null),
    designation: blankToNull(120),
    employeeCode: blankToNull(64),
    departmentId: z.number().int().positive().nullable().optional().transform((v) => v ?? null),
    role: z.enum(ROLES),
});
export const userPatchSchema = userCreateSchema
    .omit({ username: true })
    .partial()
    .extend({ isActive: z.boolean().optional() });
export const passwordChangeSchema = z
    .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z
        .string()
        .min(10, 'Use at least 10 characters')
        .max(200)
        .refine((v) => /[a-z]/.test(v), 'Include a lowercase letter')
        .refine((v) => /[A-Z]/.test(v), 'Include an uppercase letter')
        .refine((v) => /\d/.test(v), 'Include a digit'),
    confirmPassword: z.string(),
})
    .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
